import React, { useEffect, useRef, useState } from "react";

export function AgentBrowserScreencast({
  wsUrl = "ws://localhost:60675",
  width = 1200,
  height = 800,
  className = "",
}) {
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const metadataRef = useRef(null);

  const [status, setStatus] = useState({
    connected: false,
    screencasting: false,
    wsOpen: false,
  });

  const [dimensions, setDimensions] = useState({
    width, height
  })

  useEffect(() => {
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus((s) => ({ ...s, wsOpen: true }));
    };

    ws.onclose = () => {
      setStatus((s) => ({
        ...s,
        wsOpen: false,
        connected: false,
        screencasting: false,
      }));
    };

    ws.onerror = (err) => {
      console.error("Agent browser WebSocket error:", err);
    };

    ws.onmessage = async (event) => {
      let msg;

      try {
        msg = JSON.parse(event.data);
      } catch {
        console.warn("Received non-JSON WebSocket message");
        return;
      }

      if (msg.type === "status") {
        setStatus((s) => ({
          ...s,
          connected: Boolean(msg.connected),
          screencasting: Boolean(msg.screencasting),
        }));
        return;
      }

      if (msg.type === "frame") {
        metadataRef.current = msg.metadata;
        if (msg.metadata) {
          const { deviceWidth, deviceHeight } = msg.metadata
          setDimensions(deviceWidth, deviceHeight)
        }
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const img = new Image();

        img.onload = () => {
          const deviceWidth = msg.metadata?.deviceWidth || img.width;
          const deviceHeight = msg.metadata?.deviceHeight || img.height;
          ctx.drawImage(img, 0, 0, deviceWidth, deviceHeight);
        };

        img.src = `data:image/jpeg;base64,${msg.data}`;
      }
    };

    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [wsUrl]);

  function send(message, debug) {
    if (true || debug) {
      console.log("send", message)
    }
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    ws.send(JSON.stringify(message));
  }

  function getCanvasPoint(event) {
    const canvas = canvasRef.current;
    const metadata = metadataRef.current;

    if (!canvas) {
      return { x: 0, y: 0 };
    }

    const rect = canvas.getBoundingClientRect();

    const deviceWidth = metadata?.deviceWidth || canvas.width;
    const deviceHeight = metadata?.deviceHeight || canvas.height;

    const x = ((event.clientX - rect.left) / rect.width) * deviceWidth;
    const y = ((event.clientY - rect.top) / rect.height) * deviceHeight;

    return {
      x: Math.round(x),
      y: Math.round(y),
    };
  }

  function handleMouseDown(event) {
    canvasRef.current.focus()
    const { x, y } = getCanvasPoint(event);

    send({
      type: "input_mouse",
      eventType: "mousePressed",
      x,
      y,
      button: "left",
      clickCount: 1,
    });
  }

  function handleMouseUp(event) {
    const { x, y } = getCanvasPoint(event);

    send({
      type: "input_mouse",
      eventType: "mouseReleased",
      x,
      y,
      button: "left",
    });
  }

  function handleMouseMove(event) {
    const { x, y } = getCanvasPoint(event);

    send({
      type: "input_mouse",
      eventType: "mouseMoved",
      x,
      y,
    });
  }

  function handleWheel(event) {
    //event.preventDefault();

    const { x, y } = getCanvasPoint(event);

    send({
      type: "input_mouse",
      eventType: "mouseWheel",
      x,
      y,
      deltaX: event.deltaX,
      deltaY: event.deltaY,
    });
  }

  function modifierMask(event) {
    let modifiers = 0;

    if (event.altKey) modifiers |= 1;
    if (event.ctrlKey) modifiers |= 2;
    if (event.metaKey) modifiers |= 4;
    if (event.shiftKey) modifiers |= 8;

    return modifiers;
  }

  function handleKeyDown(event) {
    if (event.key.length === 1) {
      send({
        type: "input_keyboard",
        eventType: "char",
        text: event.key,
      }, true);
    } else {
      send({
        type: "input_keyboard",
        eventType: "keyDown",
        key: event.key,
        code: event.code,
        modifiers: modifierMask(event),
      }, true);
    }
  }

  function handleKeyUp(event) {
    send({
      type: "input_keyboard",
      eventType: "keyUp",
      key: event.key,
      code: event.code,
      modifiers: modifierMask(event),
    });
  }

  function touchPointsFromEvent(event) {
    const canvas = canvasRef.current;
    const metadata = metadataRef.current;

    if (!canvas) return [];

    const rect = canvas.getBoundingClientRect();

    const deviceWidth = metadata?.deviceWidth || canvas.width;
    const deviceHeight = metadata?.deviceHeight || canvas.height;

    return Array.from(event.touches).map((touch) => ({
      id: touch.identifier,
      x: Math.round(((touch.clientX - rect.left) / rect.width) * deviceWidth),
      y: Math.round(((touch.clientY - rect.top) / rect.height) * deviceHeight),
    }));
  }

  function handleTouchStart(event) {
    canvasRef.current.focus()    
    event.preventDefault();

    send({
      type: "input_touch",
      eventType: "touchStart",
      touchPoints: touchPointsFromEvent(event),
    });
  }

  function handleTouchMove(event) {
    event.preventDefault();

    send({
      type: "input_touch",
      eventType: "touchMove",
      touchPoints: touchPointsFromEvent(event),
    });
  }

  function handleTouchEnd(event) {
    event.preventDefault();

    send({
      type: "input_touch",
      eventType: "touchEnd",
      touchPoints: touchPointsFromEvent(event),
    });
  }

  return (
    <div className={className}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        tabIndex={0}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onWheel={handleWheel}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          width: "100%",
          maxWidth: width,
          aspectRatio: `${width} / ${height}`,
          background: "#111",
          border: "1px solid #333",
          borderRadius: 8,
          outline: "none",
          touchAction: "none",
          cursor: "crosshair",
        }}
      />
    </div>
  );
}
